export type UnmatchedEmailThreadRead = {
  thread_id: string;
  last_read_at: string | null;
};

export type UnmatchedEmailThreadUnreadInput = {
  id: string;
  last_inbound_at?: string | null;
  last_message_at?: string | null;
};

export function unmatchedEmailUnreadAt(
  thread: UnmatchedEmailThreadUnreadInput,
): string | null {
  return thread.last_inbound_at ?? thread.last_message_at ?? null;
}

export function isUnmatchedEmailThreadUnread(
  thread: UnmatchedEmailThreadUnreadInput,
  read: UnmatchedEmailThreadRead | null | undefined,
): boolean {
  const unreadAt = unmatchedEmailUnreadAt(thread);
  if (!unreadAt) return !read?.last_read_at;
  if (!read?.last_read_at) return true;
  return Date.parse(read.last_read_at) < Date.parse(unreadAt);
}

export function unmatchedEmailReadByThreadId(
  reads: readonly UnmatchedEmailThreadRead[],
): Map<string, UnmatchedEmailThreadRead> {
  const map = new Map<string, UnmatchedEmailThreadRead>();
  for (const read of reads) {
    map.set(read.thread_id, read);
  }
  return map;
}

export function unreadUnmatchedEmailCount(
  threads: readonly UnmatchedEmailThreadUnreadInput[],
  reads: readonly UnmatchedEmailThreadRead[],
): number {
  const byThread = unmatchedEmailReadByThreadId(reads);
  let count = 0;
  for (const thread of threads) {
    if (isUnmatchedEmailThreadUnread(thread, byThread.get(thread.id))) {
      count += 1;
    }
  }
  return count;
}

export type UnmatchedEmailListFilter = 'unread' | 'all';

export type UnmatchedEmailListItem = UnmatchedEmailThreadUnreadInput & {
  subject?: string | null;
  fromAddress?: string | null;
  preview?: string | null;
};

export function filterUnmatchedEmailThreads(
  threads: readonly UnmatchedEmailListItem[],
  reads: readonly UnmatchedEmailThreadRead[],
  opts: { tab: UnmatchedEmailListFilter; search: string },
): UnmatchedEmailListItem[] {
  const byThread = unmatchedEmailReadByThreadId(reads);
  const search = opts.search.trim().toLowerCase();
  return threads.filter((thread) => {
    if (opts.tab === 'unread' && !isUnmatchedEmailThreadUnread(thread, byThread.get(thread.id))) {
      return false;
    }
    if (!search) return true;
    const haystack = [thread.subject ?? '', thread.fromAddress ?? '', thread.preview ?? '']
      .join(' ')
      .toLowerCase();
    return haystack.includes(search);
  });
}

export function replySubjectForThread(subject: string | null | undefined): string {
  const trimmed = (subject ?? '').trim();
  if (!trimmed) return '';
  return /^re\s*:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
}
